import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './Lightbox.css';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * In-app image viewer: zoom (buttons, wheel, pinch, double-tap), pan when
 * zoomed, prev/next across a set, close via ×, Esc or backdrop tap.
 * Rendered through a portal so it sits above any open modal.
 *
 * props: images: [{ src, label }], startIndex, onClose
 */
export default function Lightbox({ images, startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map());
  const pinchDist = useRef(0);

  const current = images[idx];
  const many = images.length > 1;

  const resetView = useCallback(() => { setScale(1); setPos({ x: 0, y: 0 }); }, []);
  const go = useCallback((delta) => {
    setIdx((i) => (i + delta + images.length) % images.length);
    resetView();
  }, [images.length, resetView]);

  const zoomBy = useCallback((factor) => {
    setScale((s) => {
      const next = clamp(s * factor, MIN_SCALE, MAX_SCALE);
      if (next === 1) setPos({ x: 0, y: 0 });
      return next;
    });
  }, []);

  // Keyboard: Esc close, arrows navigate
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
      else if (e.key === 'ArrowLeft' && many) go(-1);
      else if (e.key === 'ArrowRight' && many) go(1);
      else if (e.key === '+' || e.key === '=') zoomBy(1.3);
      else if (e.key === '-') zoomBy(1 / 1.3);
    }
    document.addEventListener('keydown', onKey, true);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = '';
    };
  }, [onClose, go, many, zoomBy]);

  function onPointerDown(e) {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      pinchDist.current = Math.hypot(a.x - b.x, a.y - b.y);
    }
  }
  function onPointerMove(e) {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    const cur = { x: e.clientX, y: e.clientY };
    pointers.current.set(e.pointerId, cur);

    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchDist.current > 0) zoomBy(dist / pinchDist.current);
      pinchDist.current = dist;
    } else if (pointers.current.size === 1 && scale > 1) {
      setPos((p) => ({ x: p.x + (cur.x - prev.x), y: p.y + (cur.y - prev.y) }));
    }
  }
  function onPointerUp(e) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchDist.current = 0;
  }

  function onDoubleClick() {
    if (scale > 1) resetView();
    else setScale(2.5);
  }

  if (!current) return null;

  return createPortal(
    <div className="lightbox-overlay" onClick={onClose}>
      <button className="lightbox-close" aria-label="Close" onClick={onClose}>×</button>

      {many && (
        <>
          <button className="lightbox-nav lightbox-prev" aria-label="Previous photo"
            onClick={(e) => { e.stopPropagation(); go(-1); }}>‹</button>
          <button className="lightbox-nav lightbox-next" aria-label="Next photo"
            onClick={(e) => { e.stopPropagation(); go(1); }}>›</button>
        </>
      )}

      <div className="lightbox-stage" onClick={(e) => e.stopPropagation()}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove}
        onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
        onWheel={(e) => zoomBy(e.deltaY < 0 ? 1.15 : 1 / 1.15)}>
        <img
          src={current.src}
          alt={current.label || 'Photo'}
          draggable={false}
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})` }}
        />
      </div>

      <div className="lightbox-bar" onClick={(e) => e.stopPropagation()}>
        <button aria-label="Zoom out" onClick={() => zoomBy(1 / 1.3)}>−</button>
        <button className="lightbox-zoom-label" onClick={resetView} title="Reset zoom">{Math.round(scale * 100)}%</button>
        <button aria-label="Zoom in" onClick={() => zoomBy(1.3)}>+</button>
        <span className="lightbox-caption">
          {current.label}{many ? ` · ${idx + 1}/${images.length}` : ''}
        </span>
      </div>
    </div>,
    document.body
  );
}
